import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { ButtonGroup } from '@mui/material';
import { s } from '@/theme/tokens';
import { Box } from '@/ui';
import { CopyIcon, DeleteIcon, RotateRight, Tooltip } from '@/ui/primitives';
import { Button } from '@/ui/primitives/Button';
import { TextField } from '@/ui/primitives/TextField';

export type PipelineWebhookActionStyles = {
  actionGroupSx: Record<string, unknown>;
  actionButtonSx: Record<string, unknown>;
};

type PipelineWebhookUrlFieldProps = PipelineWebhookActionStyles & {
  webhookEndpoint: string;
  canCopy: boolean;
  onCopy: () => void;
};

export const PipelineWebhookUrlField = ({
  webhookEndpoint,
  canCopy,
  actionGroupSx,
  actionButtonSx,
  onCopy,
}: PipelineWebhookUrlFieldProps): JSX.Element => (
  <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: s('md'), width: '100%' }}>
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <TextField
        label="Webhook URL"
        fieldStyle="settings"
        value={webhookEndpoint}
        slotProps={{ input: { readOnly: true } }}
        sx={{
          '& .MuiOutlinedInput-input': {
            color: 'text.secondary',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          },
        }}
        onFocus={(event) => {
          event.target.select();
        }}
      />
    </Box>
    <ButtonGroup variant="outlined" aria-label="Webhook URL actions" sx={actionGroupSx}>
      <Button
        variant="ghost"
        fullWidthOnMobile={false}
        disableRipple
        aria-label="Copy Webhook URL"
        disabled={!canCopy}
        sx={actionButtonSx}
        onClick={onCopy}
      >
        <Tooltip title="Copy Webhook URL" placement="top">
          <CopyIcon />
        </Tooltip>
      </Button>
    </ButtonGroup>
  </Box>
);

type PipelineWebhookKeySectionProps = PipelineWebhookActionStyles & {
  value: string;
  canCopy: boolean;
  canTest: boolean;
  canGenerateOrRegenerate: boolean;
  canRevoke: boolean;
  highlightGenerateAction: boolean;
  generateLabel: string;
  onCopy: () => void;
  onTest: () => void;
  onGenerateOrRegenerate: () => void;
  onRevoke: () => void;
};

export const PipelineWebhookKeySection = ({
  value,
  canCopy,
  canTest,
  canGenerateOrRegenerate,
  canRevoke,
  highlightGenerateAction,
  generateLabel,
  actionGroupSx,
  actionButtonSx,
  onCopy,
  onTest,
  onGenerateOrRegenerate,
  onRevoke,
}: PipelineWebhookKeySectionProps): JSX.Element => (
  <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: s('md'), width: '100%' }}>
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <TextField
        fieldStyle="settings"
        value={value}
        placeholder="Generate a webhook key"
        slotProps={{ input: { readOnly: true } }}
        sx={{ '& .MuiOutlinedInput-input': { color: 'text.secondary' } }}
        onFocus={(event) => {
          event.target.select();
        }}
      />
    </Box>
    <ButtonGroup variant="outlined" aria-label="Webhook key actions" sx={actionGroupSx}>
      <Button
        variant="ghost"
        fullWidthOnMobile={false}
        disableRipple
        aria-label={generateLabel}
        disabled={!canGenerateOrRegenerate}
        sx={[
          actionButtonSx,
          ...(highlightGenerateAction
            ? [
                {
                  '&&': {
                    backgroundColor: 'primary.main',
                    color: 'primary.contrastText',
                  },
                  '&&:hover': {
                    backgroundColor: 'primary.dark',
                  },
                },
              ]
            : []),
        ]}
        onClick={onGenerateOrRegenerate}
      >
        <Tooltip title={generateLabel} placement="top">
          <RotateRight sx={{ fontSize: 21 }} />
        </Tooltip>
      </Button>
      <Button
        variant="ghost"
        fullWidthOnMobile={false}
        disableRipple
        aria-label="Test Webhook Key"
        disabled={!canTest}
        sx={[actionButtonSx, { minWidth: 56, width: 56, maxWidth: 56 }]}
        onClick={onTest}
      >
        <Tooltip title="Test Webhook Key" placement="top">
          <PlayArrowIcon sx={{ fontSize: 22 }} />
        </Tooltip>
      </Button>
      <Button
        variant="ghost"
        fullWidthOnMobile={false}
        disableRipple
        aria-label="Copy Key"
        disabled={!canCopy}
        sx={actionButtonSx}
        onClick={onCopy}
      >
        <Tooltip title="Copy Webhook Key" placement="top">
          <CopyIcon />
        </Tooltip>
      </Button>
      <Button
        variant="ghost"
        fullWidthOnMobile={false}
        disableRipple
        aria-label="Revoke Key"
        disabled={!canRevoke}
        sx={actionButtonSx}
        onClick={onRevoke}
      >
        <Tooltip title="Revoke Webhook Key" placement="top">
          <DeleteIcon sx={{ fontSize: 20 }} />
        </Tooltip>
      </Button>
    </ButtonGroup>
  </Box>
);
